import React, { useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { EuiIcon } from '@elastic/eui';
import utilsService from '../../../../../servises/utils.service';

// Keyframes
const slide = keyframes`
  0% { left: -30px; }
  100% { left: 100%; }
`;

// Styled Components
const CardWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 4px 0;
`;

const CpuHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  background: rgba(255, 255, 255, 0.02);
  border: 1px solid rgba(255, 255, 255, 0.05);
  border-radius: 8px;
  padding: 12px;
  
  .brand-row {
    display: flex;
    align-items: center;
    gap: 8px;
    
    .brand-icon {
      color: ${props => props.brandColor || '#9f7aea'};
      background: ${props => props.brandBg || 'rgba(159, 122, 234, 0.1)'};
      padding: 6px;
      border-radius: 6px;
      display: flex;
      align-items: center;
      justify-content: center;
    }
    
    .brand-label {
      font-size: 11px;
      text-transform: uppercase;
      letter-spacing: 0.8px;
      font-weight: 700;
      color: ${props => props.brandColor || '#9f7aea'};
    }
  }
  
  .cpu-model {
    font-size: 13px;
    font-weight: 700;
    color: #fff;
    line-height: 1.4;
    word-break: break-word;
    margin-top: 4px;
  }
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-gap: 8px;
`;

const StatItem = styled.div`
  background: rgba(255, 255, 255, 0.01);
  border: 1px solid rgba(255, 255, 255, 0.03);
  border-radius: 6px;
  padding: 8px 10px;
  display: flex;
  flex-direction: column;
  gap: 2px;
  
  .label {
    font-size: 10px;
    color: rgba(255, 255, 255, 0.45);
    font-weight: 500;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }
  
  .value {
    font-size: 13px;
    font-weight: 700;
    color: #fff;
    font-family: Consolas, monospace;
    word-break: break-all;
  }
`;

const UtilizationCard = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: rgba(255, 255, 255, 0.02);
  border: 1px solid rgba(255, 255, 255, 0.04);
  border-radius: 8px;
  padding: 12px 14px;
  
  .gauge-section {
    display: flex;
    align-items: center;
    gap: 12px;
    
    .percentage {
      font-size: 20px;
      font-weight: 800;
      color: #fff;
      font-family: Consolas, monospace;
      display: flex;
      flex-direction: column;
      
      .pct-label {
        font-size: 9px;
        color: rgba(255, 255, 255, 0.4);
        font-weight: 600;
        letter-spacing: 0.5px;
        text-transform: uppercase;
      }
    }
  }
  
  .track-section {
    flex-grow: 1;
    height: 4px;
    background: rgba(255, 255, 255, 0.05);
    border-radius: 2px;
    position: relative;
    overflow: hidden;
    margin-left: 16px;
    
    .scanner-dot {
      position: absolute;
      height: 100%;
      width: 40px;
      background: linear-gradient(90deg, transparent, ${props => props.color}, transparent);
      animation: ${slide} ${props => props.speed || 4}s infinite linear;
    }
  }
`;

const CollapsibleSection = styled.details`
  margin-top: 10px;
  background: rgba(255, 255, 255, 0.02);
  border: 1px solid rgba(255, 255, 255, 0.05);
  border-radius: 8px;
  padding: 4px 12px;
  transition: all 0.2s ease;

  &[open] {
    background: rgba(255, 255, 255, 0.03);
    border-color: rgba(255, 255, 255, 0.08);
  }
`;

const Summary = styled.summary`
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.8px;
  color: rgba(255, 255, 255, 0.6);
  padding: 8px 0;
  cursor: pointer;
  user-select: none;
  display: flex;
  align-items: center;
  gap: 6px;
  list-style: none;
  
  &::-webkit-details-marker {
    display: none;
  }

  &:hover {
    color: #fff;
  }

  .arrow-icon {
    color: var(--color-primary, #6366f1);
  }
`;

const DetailsContent = styled.div`
  padding-top: 6px;
  padding-bottom: 8px;
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-gap: 8px;
`;

// Helper components
const CircularGauge = ({ value, color }) => {
    const radius = 22;
    const stroke = 4;
    const normalizedRadius = radius - stroke * 2;
    const circumference = normalizedRadius * 2 * Math.PI;
    const strokeDashoffset = circumference - (Math.min(100, Math.max(0, value)) / 100) * circumference;

    return (
        <svg height={radius * 2} width={radius * 2} style={{ transform: 'rotate(-90deg)', display: 'block' }}>
            {/* Background track */}
            <circle
                stroke="rgba(255,255,255,0.06)"
                fill="transparent"
                strokeWidth={stroke}
                r={normalizedRadius}
                cx={radius}
                cy={radius}
            />
            {/* Foreground fill */}
            <circle
                stroke={color}
                fill="transparent"
                strokeWidth={stroke}
                strokeDasharray={circumference + ' ' + circumference}
                style={{ strokeDashoffset, transition: 'stroke-dashoffset 0.5s ease' }}
                r={normalizedRadius}
                cx={radius}
                cy={radius}
            />
        </svg>
    );
};

const getCpuBrandStyle = cpuName => {
    const name = (cpuName || "").toLowerCase();
    if (name.includes("intel")) {
        return {
            color: "#0071C5",
            bg: "rgba(0, 113, 197, 0.15)",
            displayName: "Intel® Core™",
            icon: "logoTerminal"
        };
    }
    if (name.includes("amd") || name.includes("ryzen")) {
        return {
            color: "#ED1C24",
            bg: "rgba(237, 28, 36, 0.15)",
            displayName: "AMD® Ryzen™",
            icon: "logoTerminal"
        };
    }
    if (name.includes("apple") || name.includes("m1") || name.includes("m2") || name.includes("m3")) {
        return {
            color: "#ffffff",
            bg: "rgba(255, 255, 255, 0.1)",
            displayName: "Apple Silicon",
            icon: "logoMac"
        };
    }
    return {
        color: "#9f7aea",
        bg: "rgba(159, 122, 234, 0.1)",
        displayName: "Processor",
        icon: "compute"
    };
};

const getLoadColorAndSpeed = load => {
    let color = "rgb(0, 128, 213)";
    let speed = 4; // slower animation for low load
    
    if (load > 70) {
        color = "rgb(249, 190, 73)";
        speed = 2;
    }
    if (load > 90) {
        color = "rgb(248, 107, 99)";
        speed = 0.8; // super fast animation for critical load
    }
    
    return { color, speed };
};

const ServerCpuWidget = ({ server }) => {
    const cpuName = utilsService.getFromMetrics(server, 'cpu_name', 'Generic CPU');
    const cpuFreq = utilsService.getFromMetrics(server, 'cpu_freq', '-');
    const cpuCores = utilsService.getFromMetrics(server, 'cpu_cores', '-');
    const cpuLoad = parseInt(utilsService.getFromMetrics(server, 'load_cpu', 0), 10);
    const loadAvg = utilsService.getFromMetrics(server, 'load', '-');
    const loadIo = utilsService.getFromMetrics(server, 'load_io', '-');

    const brandStyle = getCpuBrandStyle(cpuName);
    const loadStyle = getLoadColorAndSpeed(cpuLoad);

    return (
        <CardWrapper>
            {/* CPU Brand Header */}
            <CpuHeader brandColor={ brandStyle.color } brandBg={ brandStyle.bg }>
                <div className="brand-row">
                    <div className="brand-icon">
                        <EuiIcon type={ brandStyle.icon || "compute" } size="s" />
                    </div>
                    <span className="brand-label">{ brandStyle.displayName }</span>
                </div>
                <div className="cpu-model">{ cpuName }</div>
            </CpuHeader>

            {/* Load Gauge Card */}
            <UtilizationCard color={ loadStyle.color } speed={ loadStyle.speed }>
                <div className="gauge-section">
                    <CircularGauge value={ cpuLoad } color={ loadStyle.color } />
                    <div className="percentage">
                        <span className="pct-label">Utilization</span>
                        <span>{ cpuLoad }%</span>
                    </div>
                </div>
                <div className="track-section">
                    <div className="scanner-dot" />
                </div>
            </UtilizationCard>

            {/* Frequency, Cores, Load Avg, and IO Wait */}
            <StatsGrid>
                <StatItem>
                    <span className="label">Frequency</span>
                    <span className="value">{ cpuFreq }</span>
                </StatItem>
                <StatItem>
                    <span className="label">Cores</span>
                    <span className="value">{ cpuCores } Cores</span>
                </StatItem>
                <StatItem>
                    <span className="label">Load Avg</span>
                    <span className="value" style={{ fontSize: '11px' }} title={ loadAvg }>{ loadAvg }</span>
                </StatItem>
                <StatItem>
                    <span className="label">IO Wait</span>
                    <span className="value">{ loadIo !== '-' ? `${loadIo}%` : '-' }</span>
                </StatItem>
            </StatsGrid>
        </CardWrapper>
    );
};

export default ServerCpuWidget;
